import { useState, useEffect, useMemo } from 'react'
import { Box, Chip, FormControl, InputLabel, Select, MenuItem, OutlinedInput, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FiltersContainer } from './styledComponents'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getChamberRoomsWithDetails } from 'src/supportPortal/services/rosterManagementService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { EVENT_TYPE_SCREENING, EVENT_TYPE_THERAPY_SESSION } from 'src/consumer/constants'
import ClinicRoomHierarchicalSelect from './ClinicRoomHierarchicalSelect'

export default function RosterFilters({
  selectedRoomIds,
  setSelectedRoomIds,
  selectedTargetSpecialitySlugs,
  setSelectedTargetSpecialitySlugs,
  selectedEventTypes,
  setSelectedEventTypes,
  allowedClinicIds,
  allowedClinics,
  isRosterAdmin,
  onRoomsDataChange, // Callback to pass full room data to parent
  onTargetSpecialitiesChange, // Callback to pass target specialities to parent
}) {
  const [allRooms, setAllRooms] = useState([])
  const [targetSpecialities, setTargetSpecialities] = useState([])
  const [loading, setLoading] = useState(false)

  // Event type options
  const eventTypeOptions = [
    { id: EVENT_TYPE_SCREENING, label: 'Screening' },
    { id: EVENT_TYPE_THERAPY_SESSION, label: 'Therapy Session' },
  ]

  // Fetch all rooms upfront (filtered by allowed clinics for managers and by Target Speciality/Event Type filters)
  useEffect(() => {
    async function fetchAllRooms() {
      setLoading(true)
      try {
        // For managers, filter by allowed clinic brand IDs
        // For admins, fetch all rooms
        const clinicBrandIds = isRosterAdmin ? null : allowedClinicIds

        // Combine targetSpecialitySlugs from:
        // 1. User-selected filters (selectedTargetSpecialitySlugs)
        // 2. Allowed clinics for managers (if no user selection)
        let targetSpecialitySlugs = selectedTargetSpecialitySlugs && selectedTargetSpecialitySlugs.length > 0 
          ? selectedTargetSpecialitySlugs 
          : null

        // If no user selection and manager, use allowed clinics' targetSpecialitySlugs
        if (!targetSpecialitySlugs && !isRosterAdmin && allowedClinics && allowedClinics.length > 0) {
          // Collect all unique targetSpecialitySlugs from allowed clinics
          const allSlugs = new Set()
          allowedClinics.forEach((clinic) => {
            if (clinic.targetSpecialitySlugs && Array.isArray(clinic.targetSpecialitySlugs)) {
              clinic.targetSpecialitySlugs.forEach((slug) => allSlugs.add(slug))
            }
          })
          // Only use targetSpecialitySlugs if at least one clinic has them specified
          // If all clinics have empty/missing targetSpecialitySlugs, it means all specialities allowed
          if (allSlugs.size > 0) {
            targetSpecialitySlugs = Array.from(allSlugs)
          }
        }

        // Use selectedEventTypes as preferredEventTypes to filter rooms by RosterSchedule.eventTypes
        const preferredEventTypes = selectedEventTypes && selectedEventTypes.length > 0 
          ? selectedEventTypes 
          : null

        const resp = await getChamberRoomsWithDetails({
          clinicBrandIds,
          targetSpecialitySlugs, // Filter by target speciality slugs (user selection or allowed clinics)
          preferredEventTypes, // Filter by event types from user selection
          rostered: true, // Explicitly request only rostered rooms
          includeAssociatedMentors: true, // Include mentors filtered by RosterSchedule.eventTypes
        })

        if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)?.['chamber-room'] || []
          
          // Additional client-side filter as safety check (in case API doesn't filter)
          // Filter to only include rooms where rostered === true
          const rosteredRooms = data.filter((room) => room.rostered === true)
          setAllRooms(rosteredRooms)
          
          // Pass full room data to parent component for accessing mentorsAndAuditors
          if (onRoomsDataChange) {
            onRoomsDataChange(rosteredRooms)
          }
        }
      } catch (err) {
        console.error('Error fetching rooms:', err)
      } finally {
        setLoading(false)
      }
    }

    // Only fetch if admin or if manager has allowed clinics
    if (isRosterAdmin || (allowedClinicIds && allowedClinicIds.length > 0)) {
      fetchAllRooms()
    } else if (!isRosterAdmin && (!allowedClinicIds || allowedClinicIds.length === 0)) {
      // Manager with no allowed clinics
      setAllRooms([])
      setSelectedRoomIds([]) // Clear selected rooms when no clinics available
    }
  }, [isRosterAdmin, allowedClinicIds, allowedClinics, selectedTargetSpecialitySlugs, selectedEventTypes])

  // Fetch target specialties
  useEffect(() => {
    async function fetchTargetSpecialities() {
      setLoading(true)
      try {
        const resp = await getSPDataWithParams('/api/secure/cc/support/target-specialties/page', {
          includeDisabled: false,
          page: 0,
          size: 200,
        })
        
        if (resp.status === 200) {
          let specialties = []
          
          // Handle different response structures
          // Check if resp.data is directly an array (non-CommonResponse format)
          if (Array.isArray(resp.data)) {
            specialties = resp.data
          } else if (isCommonResponseSuccessfulV2(resp)) {
            // CommonResponse format: { code: 200, data: {...} }
            // getDataMapFromCommonResponse returns resp?.data?.data
            const data = getDataMapFromCommonResponse(resp)
            
            // Handle different nested structures:
            // 1. Paginated response: { content: [...] }
            // 2. Direct array in data.data: [...]
            // 3. Direct array in data: [...]
            // 4. Object with targetSpecialities key: { targetSpecialities: [...] }
            if (Array.isArray(data)) {
              // Array from getDataMapFromCommonResponse
              specialties = data
            } else if (data?.content && Array.isArray(data.content)) {
              // Paginated response with content property
              specialties = data.content
            } else if (data?.targetSpecialities && Array.isArray(data.targetSpecialities)) {
              // Object with targetSpecialities key
              specialties = data.targetSpecialities
            } else if (data && typeof data === 'object') {
              // Try to find any array property
              const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]))
              if (arrayKey) {
                specialties = data[arrayKey]
              }
            }
            
            // Fallback: check resp.data directly if getDataMapFromCommonResponse didn't work
            if (specialties.length === 0 && resp.data && typeof resp.data === 'object') {
              if (Array.isArray(resp.data)) {
                specialties = resp.data
              } else if (resp.data?.content && Array.isArray(resp.data.content)) {
                specialties = resp.data.content
              } else if (resp.data?.data && Array.isArray(resp.data.data)) {
                specialties = resp.data.data
              }
            }
          } else {
            // Not CommonResponse format - check nested structures
            if (resp.data?.content && Array.isArray(resp.data.content)) {
              specialties = resp.data.content
            } else if (resp.data?.data && Array.isArray(resp.data.data)) {
              specialties = resp.data.data
            }
          }
          
          setTargetSpecialities(specialties)
        } else {
          console.error('Failed to fetch target specialties:', resp)
        }
      } catch (err) {
        console.error('Error fetching target specialties:', err)
      } finally {
        setLoading(false)
      }
    }

      fetchTargetSpecialities()
    }, [])

  // Pass target specialities to parent when they change
  useEffect(() => {
    if (onTargetSpecialitiesChange) {
      onTargetSpecialitiesChange(targetSpecialities)
    }
  }, [targetSpecialities, onTargetSpecialitiesChange])

  // Filter selectedRoomIds when allRooms changes (e.g., when filters are applied)
  // This ensures selected rooms are cleared if they no longer match the filters
  useEffect(() => {
    if (selectedRoomIds && selectedRoomIds.length > 0 && allRooms.length > 0) {
      const validRoomIds = allRooms.map(room => room.id)
      const filteredSelectedIds = selectedRoomIds.filter(id => validRoomIds.includes(id))
      if (filteredSelectedIds.length !== selectedRoomIds.length) {
        setSelectedRoomIds(filteredSelectedIds)
      }
    } else if (allRooms.length === 0 && selectedRoomIds && selectedRoomIds.length > 0) {
      // If no rooms available, clear selection
      setSelectedRoomIds([])
    }
  }, [allRooms, selectedRoomIds, setSelectedRoomIds])

  const handleRoomChange = (selectedRoomIds) => {
    setSelectedRoomIds(selectedRoomIds)
  }

  const handleTargetSpecialityChange = (event) => {
    const value = event.target.value
    setSelectedTargetSpecialitySlugs(typeof value === 'string' ? value.split(',') : value)
  }

  const handleEventTypeChange = (event) => {
    const value = event.target.value
    setSelectedEventTypes(typeof value === 'string' ? value.split(',') : value)
  }

  // Helper to render filter with clear button
  const renderFilterWithClear = (
    label,
    value,
    onClear,
    children,
    disabled = false,
    isCustomComponent = false, // If true, children already includes FormControl
  ) => {
    const hasValue = Array.isArray(value) ? value.length > 0 : value !== null && value !== ''
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isCustomComponent ? (
          children
        ) : (
          <FormControl sx={{ minWidth: 200 }} disabled={disabled}>
            <InputLabel>{label}</InputLabel>
            {children}
          </FormControl>
        )}
        {hasValue && (
          <IconButton
            size="small"
            onClick={onClear}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main', backgroundColor: 'action.hover' },
            }}
            title={`Clear ${label}`}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    )
  }

  return (
    <FiltersContainer>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Combined Clinic/Location/Room Hierarchical Filter */}
        {renderFilterWithClear(
          'Clinic & Room',
          selectedRoomIds,
          () => setSelectedRoomIds([]),
          <ClinicRoomHierarchicalSelect
            rooms={allRooms}
            selectedRoomIds={selectedRoomIds}
            onChange={handleRoomChange}
            label="Clinic & Room"
            disabled={loading || allRooms.length === 0}
            multiple={true}
          />,
          loading || allRooms.length === 0,
          true, // isCustomComponent - already has FormControl
        )}

        {/* Target Speciality Filter */}
        {renderFilterWithClear(
          'Target Speciality',
          selectedTargetSpecialitySlugs,
          () => setSelectedTargetSpecialitySlugs([]),
          <Select
            multiple
            value={selectedTargetSpecialitySlugs}
            onChange={handleTargetSpecialityChange}
            input={<OutlinedInput label="Target Speciality" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const specialty = targetSpecialities.find((s) => s.slug === value || s.id === value)
                  return <Chip key={value} label={specialty?.name || specialty?.title || specialty?.slug || value} size="small" />
                })}
              </Box>
            )}
          >
            {targetSpecialities.map((specialty) => {
              const value = specialty.slug || specialty.id
              const label = specialty.name || specialty.title || specialty.slug || value
              return (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              )
            })}
          </Select>,
        )}

        {/* Event Type Filter */}
        {renderFilterWithClear(
          'Event Type',
          selectedEventTypes,
          () => setSelectedEventTypes([]),
          <Select
            multiple
            value={selectedEventTypes}
            onChange={handleEventTypeChange}
            input={<OutlinedInput label="Event Type" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const eventType = eventTypeOptions.find((e) => e.id === value)
                  return <Chip key={value} label={eventType?.label || value} size="small" />
                })}
              </Box>
            )}
          >
            {eventTypeOptions.map((eventType) => (
              <MenuItem key={eventType.id} value={eventType.id}>
                {eventType.label}
              </MenuItem>
            ))}
          </Select>,
        )}
      </Box>
    </FiltersContainer>
  )
}
